/**
 * Layout 레이아웃 관리 Service
 * 비즈니스 로직 및 계산 함수
 */

import type { GridPosition, SeatDirection, DeviceStatus } from '../models/Layout';

export class LayoutService {
  // ==================== Grid & Position ====================

  /**
   * 좌석 방향에 따른 CSS rotation 클래스 반환
   */
  getSeatRotation(direction: SeatDirection): string {
    switch (direction) {
      case 'north':
        return 'rotate-0';
      case 'south':
        return 'rotate-180';
      case 'east':
        return 'rotate-90';
      case 'west':
        return '-rotate-90';
      default:
        return 'rotate-0';
    }
  }

  /**
   * 두 점 사이의 모든 그리드 위치 계산 (범위 선택)
   */
  getPositionsInRange(start: GridPosition, end: GridPosition): GridPosition[] {
    const positions: GridPosition[] = [];
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        positions.push({ x, y });
      }
    }

    return positions;
  }

  /**
   * 배경색에 따른 적절한 텍스트 색상 결정 (대비)
   */
  getTextColorForBackground(backgroundColor: string): string {
    // hex 색상을 RGB로 변환
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // 밝기 계산 (0.299*R + 0.587*G + 0.114*B)
    const brightness = r * 0.299 + g * 0.587 + b * 0.114;

    // 밝기가 128보다 크면 검은색, 작으면 흰색
    return brightness > 128 ? '#000000' : '#FFFFFF';
  }

  // ==================== Device Status ====================

  /**
   * 기기 상태에 따른 색상 반환
   */
  getDeviceStatusColor(status: DeviceStatus): string {
    switch (status) {
      case 'online':
        return 'bg-green-500';
      case 'offline':
        return 'bg-gray-400';
      case 'warning':
        return 'bg-yellow-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  }

  /**
   * 기기 상태에 따른 레이블 반환
   */
  getDeviceStatusLabel(status: DeviceStatus): string {
    switch (status) {
      case 'online':
        return '정상';
      case 'offline':
        return '오프라인';
      case 'warning':
        return '경고';
      case 'error':
        return '오류';
      default:
        return '알 수 없음';
    }
  }

  /**
   * 기기 타입에 따른 아이콘 반환
   */
  getDeviceTypeIcon(deviceType: string): string {
    switch (deviceType) {
      case 'face_recognition_gate':
        return '🚪';
      case 'kiosk':
        return '💻';
      case 'printer':
        return '🖨️';
      case 'air_purifier':
        return '💨';
      case 'cctv':
        return '📹';
      default:
        return '📱';
    }
  }

  /**
   * 기기 타입에 따른 레이블 반환
   */
  getDeviceTypeLabel(deviceType: string): string {
    switch (deviceType) {
      case 'face_recognition_gate':
        return '얼굴인식 게이트';
      case 'kiosk':
        return '키오스크';
      case 'printer':
        return '프린터';
      case 'air_purifier':
        return '공기청정기';
      case 'cctv':
        return 'CCTV';
      default:
        return '기기';
    }
  }

  // ==================== Validation ====================

  /**
   * 구역 생성/수정 데이터 유효성 검증
   */
  validateZoneData(data: { name: string; premium_rate: number }): {
    valid: boolean;
    message?: string;
  } {
    if (!data.name || data.name.trim() === '') {
      return { valid: false, message: '구역 이름을 입력해주세요.' };
    }

    if (data.premium_rate < 0.1 || data.premium_rate > 5.0) {
      return { valid: false, message: '프리미엄 배율은 0.1 ~ 5.0 사이여야 합니다.' };
    }

    return { valid: true };
  }

  /**
   * 기기 생성 데이터 유효성 검증
   */
  validateDeviceData(data: { device_name?: string; room_id?: string }): {
    valid: boolean;
    message?: string;
  } {
    if (!data.room_id) {
      return { valid: false, message: '룸을 선택해주세요.' };
    }

    if (!data.device_name || data.device_name.trim() === '') {
      return { valid: false, message: '기기명을 입력해주세요.' };
    }

    return { valid: true };
  }

  // ==================== Formatting ====================

  /**
   * 좌석 타입에 따른 레이블 반환
   */
  getSeatTypeLabel(seatType: string): string {
    switch (seatType) {
      case 'standard':
        return '일반석';
      case 'premium':
        return '프리미엄석';
      case 'deluxe':
        return '디럭스석';
      default:
        return '일반석';
    }
  }

  /**
   * 칸막이 높이에 따른 레이블 반환
   */
  getPartitionHeightLabel(height: string): string {
    switch (height) {
      case 'low':
        return '낮음';
      case 'medium':
        return '중간';
      case 'high':
        return '높음';
      default:
        return '중간';
    }
  }
}

export const layoutService = new LayoutService();
